const CANARY_SIZE_PX = 8;
const CANARY_HALF_PX = CANARY_SIZE_PX / 2;
const CHANNELS_PER_PIXEL = 4;
const RGB_CHANNELS = 3;
// Wide enough for low-bit noise (Brave farbling), far too narrow for a solid fill to match.
const CHANNEL_TOLERANCE = 12;

type Rgb = readonly [number, number, number];

// Fixed and opaque so the expected readback never depends on signer content.
const CANARY_QUADRANTS: readonly { x: number; y: number; color: Rgb }[] = [
  { x: 0, y: 0, color: [255, 0, 0] },
  { x: CANARY_HALF_PX, y: 0, color: [0, 255, 0] },
  { x: 0, y: CANARY_HALF_PX, color: [0, 0, 255] },
  { x: CANARY_HALF_PX, y: CANARY_HALF_PX, color: [255, 255, 0] },
];

export type CanvasReadbackResult =
  | { ok: true }
  | { ok: false; reason: 'uniform' | 'blank' | 'unreadable' | 'mismatch' };

const expectedColorAt = (x: number, y: number): Rgb => {
  const quadrant = CANARY_QUADRANTS.find(
    (q) =>
      x >= q.x &&
      x < q.x + CANARY_HALF_PX &&
      y >= q.y &&
      y < q.y + CANARY_HALF_PX,
  );
  return quadrant ? quadrant.color : [0, 0, 0];
};

export const evaluateCanvasReadback = (
  pixels: ArrayLike<number>,
): CanvasReadbackResult => {
  const expectedLength = CANARY_SIZE_PX * CANARY_SIZE_PX * CHANNELS_PER_PIXEL;
  if (pixels.length !== expectedLength) {
    return { ok: false, reason: 'unreadable' };
  }

  let allZero = true;
  let uniform = true;
  for (let i = 0; i < pixels.length; i++) {
    if (pixels[i] !== 0) allZero = false;
    if (
      i >= CHANNELS_PER_PIXEL &&
      pixels[i] !== pixels[i % CHANNELS_PER_PIXEL]
    ) {
      uniform = false;
    }
  }
  if (allZero) return { ok: false, reason: 'blank' };
  if (uniform) return { ok: false, reason: 'uniform' };

  for (let y = 0; y < CANARY_SIZE_PX; y++) {
    for (let x = 0; x < CANARY_SIZE_PX; x++) {
      const offset = (y * CANARY_SIZE_PX + x) * CHANNELS_PER_PIXEL;
      const expected = expectedColorAt(x, y);
      for (let c = 0; c < RGB_CHANNELS; c++) {
        if (Math.abs(pixels[offset + c] - expected[c]) > CHANNEL_TOLERANCE) {
          return { ok: false, reason: 'mismatch' };
        }
      }
    }
  }
  return { ok: true };
};

export const checkCanvasReadback = (): CanvasReadbackResult => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = CANARY_SIZE_PX;
    canvas.height = CANARY_SIZE_PX;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { ok: false, reason: 'unreadable' };

    CANARY_QUADRANTS.forEach(({ x, y, color }) => {
      const [red, green, blue] = color;
      ctx.fillStyle = `rgb(${red}, ${green}, ${blue})`;
      ctx.fillRect(x, y, CANARY_HALF_PX, CANARY_HALF_PX);
    });

    const { data } = ctx.getImageData(0, 0, CANARY_SIZE_PX, CANARY_SIZE_PX);
    return evaluateCanvasReadback(data);
  } catch {
    return { ok: false, reason: 'unreadable' };
  }
};
